import React, { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import ImageCropDialog from "../ui/ImageCropDialog";

interface FileUploadProps {
  file: File | null;
  setFile: (file: File | null) => void;
  label: string;
  addText: string;
  shape?: "circle" | "rectangle";
  size?: number | string;
  initial?: string;
}

const FileUpload: React.FC<FileUploadProps> = ({
  file,
  setFile,
  label,
  addText,
  shape = "rectangle",
  size,
  initial,
}) => {
  const [preview, setPreview] = useState<string | null>(initial ?? null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
const [imageAspect, setImageAspect] = useState<number>(16 / 9);

  const isCircle = shape === "circle";

  useEffect(() => {
    if (file) setPreview(URL.createObjectURL(file));
  }, [file]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0]) return;
    const selectedFile = e.target.files[0];
    const maxSizeInMB = 1;

    if (selectedFile.size > maxSizeInMB * 1024 * 1024) {
      alert(`File is too large. Max size is ${maxSizeInMB} MB`);
      return;
    }

    // setCropSrc(URL.createObjectURL(selectedFile)); // Open crop dialog
     const img = new Image();
      img.onload = () => {
        const ratio = img.width / img.height;
        setImageAspect(ratio); // store the actual ratio
        setCropSrc(URL.createObjectURL(selectedFile)); // open crop dialog
      };
      img.src = URL.createObjectURL(selectedFile);
  };

  const handleCropSave = (croppedFile: File) => {
    setFile(croppedFile);
    setPreview(URL.createObjectURL(croppedFile));
    setCropSrc(null);
  };

  return (
    <div className="flex flex-col items-center gap-2">
      <span className="text-sm font-medium text-black capitalize">{label}</span>
      <div
        className={`relative border-[3px] border-dashed border-gray-300 flex justify-center items-center bg-white cursor-pointer hover:border-primary ${
          isCircle ? "rounded-full overflow-hidden" : "rounded-lg overflow-auto"
        }`}
        style={{
          width: isCircle ? size || 128 : "100%",
          height: isCircle ? size || 128 : size || 192,
        }}
      >
        {preview ? (
          <img
            src={preview}
            alt="Preview"
            className={`w-full ${isCircle ? "h-full object-cover" : "object-contain"}`}
          />
        ) : (
          <div className="flex flex-col justify-center items-center text-gray-400">
            <Plus size={isCircle ? 24 : 32} />
            <span className="text-sm mt-1 capitalize">{addText}</span>
          </div>
        )}

        <input
          type="file"
          accept="image/*"
          onChange={handleChange}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
      </div>

      {/* Reusable crop dialog */}
      {cropSrc && (
        <ImageCropDialog
          src={cropSrc}
          aspect={isCircle ? 1 :  imageAspect}
          onClose={() => setCropSrc(null)}
          onSave={handleCropSave}
        />
      )}
    </div>
  );
};

export default FileUpload;
